<template>
    <div class="automation-emails">
        <Box
            title="Envios Automatizados"
            sub-title="Configure automações para envio de documentos por email"
            icon="mail"
            :variant="BOX_VARIANTS.DEFAULT"
            :actions="headerActions"
            @action-click="handleHeaderAction"
        >

            <!-- Cards de estatísticas -->
            <div class="stats-section">
                <div class="row">
                    <!-- Envios Este Mês -->
                    <div class="grid_col_3 grid_col_m_6 grid_col_s_12 grid_col_xs_12">
                        <div class="stat-card stat-card--success">
                            <div class="stat-header">
                                <div class="stat-icon">
                                    <sw-icon name="mail" />
                                </div>
                                <div class="stat-info">
                                    <span class="stat-label">Envios Este Mês</span>
                                    <div class="stat-numbers">
                                        <span class="stat-main">{{ emailsSent }}</span>
                                        <span class="stat-secondary">/ {{ monthlyLimit }} emails</span>
                                    </div>
                                </div>
                            </div>
                            <div class="stat-progress">
                                <div class="progress-bar">
                                    <div class="progress-fill" :style="{ width: (emailsSent / monthlyLimit * 100) + '%' }"></div>
                                </div>
                                <span class="progress-percentage">{{ Math.round(emailsSent / monthlyLimit * 100) }}% usado</span>
                            </div>
                        </div>
                    </div>

                    <!-- Automações Ativas -->
                    <div class="grid_col_3 grid_col_m_6 grid_col_s_12 grid_col_xs_12">
                        <div class="stat-card stat-card--primary">
                            <div class="stat-header">
                                <div class="stat-icon">
                                    <sw-icon name="mail2" />
                                </div>
                                <div class="stat-info">
                                    <span class="stat-label">Automações Ativas</span>
                                    <div class="stat-numbers">
                                        <span class="stat-main">{{ activeAutomationsCount }}</span>
                                        <span class="stat-secondary">de {{ automations.length }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Programados -->
                    <div class="grid_col_3 grid_col_m_6 grid_col_s_12 grid_col_xs_12">
                        <div class="stat-card stat-card--compact">
                            <div class="stat-header">
                                <div class="stat-icon">
                                    <sw-icon name="clock" />
                                </div>
                                <div class="stat-info">
                                    <span class="stat-label">Programados</span>
                                    <div class="stat-numbers">
                                        <span class="stat-main">{{ emailsScheduled }}</span>
                                        <span class="stat-secondary">próximos</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Seção de Filtros -->
            <FilterSection
                label="Filtrar por:"
                search-placeholder="Buscar automações..."
                :search-value="searchQuery"
                :filters="filterOptions"
                :filter-values="filterValues"
                @update:search="searchQuery = $event"
                @update:filter="updateFilter"
                @clear-filters="clearFilters"
            />

        </Box>

        <!-- Lista de automações -->
        <div class="automations-section" v-if="filteredAutomations.length > 0">
            <div class="row">
                <div
                    v-for="automation in filteredAutomations"
                    :key="automation.id"
                    class="grid_col_4 grid_col_m_6 grid_col_s_12 grid_col_xs_12"
                >
                    <div
                        class="automation-card card-base"
                        :class="{ 'card-base--inactive': !automation.active }"
                    >
                        <div class="card-status-bar" :class="'card-status-bar--' + getStatusClass(automation)"></div>

                        <div class="card-header">
                            <div class="automation-header-content">
                                <div class="automation-actions">
                                    <div class="status-badge" :class="'status-badge--' + getStatusClass(automation)">
                                        <span class="status-dot"></span>
                                        {{ getStatusLabel(automation) }}
                                    </div>
                                    <div class="action-buttons">
                                        <button
                                            v-if="automation.active"
                                            class="action-btn action-btn--toggle pause"
                                            @click="toggleAutomation(automation)"
                                            title="Pausar automação"
                                        >
                                            <sw-icon name="pause" />
                                        </button>
                                        <button
                                            v-else
                                            class="action-btn action-btn--toggle play"
                                            @click="toggleAutomation(automation)"
                                            title="Ativar automação"
                                        >
                                            <sw-icon name="play" />
                                        </button>
                                        <button
                                            class="action-btn action-btn--edit"
                                            @click="editAutomation(automation)"
                                            title="Editar automação"
                                        >
                                            <sw-icon name="pencil" />
                                        </button>
                                        <button
                                            class="action-btn action-btn--delete"
                                            @click="deleteAutomation(automation)"
                                            title="Excluir automação"
                                        >
                                            <sw-icon name="bin" />
                                        </button>
                                    </div>
                                </div>
                                <div class="automation-title-section">
                                    <h4>{{ automation.name }}</h4>
                                </div>
                            </div>
                            <div class="automation-description">
                                <p>{{ automation.description }}</p>
                            </div>

                            <!-- Seção especial para automações aguardando arquivos -->
                            <div class="waiting-section" v-if="automation.waiting">
                                <div class="waiting-info">
                                    <div class="waiting-header">
                                        <sw-icon name="cloud" />
                                        <span>Aguardando uploads para ativação</span>
                                    </div>
                                    <div class="waiting-details">
                                        <div class="upload-link">
                                            <span class="link-label">Link de upload:</span>
                                            <a :href="automation.uploadPageUrl" target="_blank" class="upload-url">
                                                {{ automation.uploadPageUrl }}
                                                <sw-icon name="link" />
                                            </a>
                                        </div>
                                        <div class="deadline-info" v-if="automation.uploadDeadline">
                                            <sw-icon name="clock" />
                                            <span>Prazo: {{ formatDate(automation.uploadDeadline) }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="expected-files" v-if="automation.expectedFiles && automation.expectedFiles.length > 0">
                                    <span class="expected-label">Arquivos esperados:</span>
                                    <div class="files-list">
                                        <span
                                            v-for="file in automation.expectedFiles"
                                            :key="file"
                                            class="file-tag"
                                        >
                                            {{ file }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="card-content" v-if="!automation.waiting">
                            <div class="automation-metrics">
                                <div class="metric-row">
                                    <div class="detail-item detail-item--primary">
                                        <div class="detail-icon">
                                            <sw-icon name="clock" />
                                        </div>
                                        <div class="detail-content">
                                            <span class="detail-label">Frequência</span>
                                            <span class="detail-value">{{ formatFrequency(automation.frequency) }}</span>
                                        </div>
                                    </div>
                                    <div class="detail-item detail-item--secondary">
                                        <div class="detail-icon">
                                            <sw-icon name="mail" />
                                        </div>
                                        <div class="detail-content">
                                            <span class="detail-label">Este mês</span>
                                            <span class="detail-value">{{ automation.monthlyCount }} envios</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="metric-row">
                                    <div class="detail-item detail-item--info">
                                        <div class="detail-icon">
                                            <sw-icon name="file-text" />
                                        </div>
                                        <div class="detail-content">
                                            <span class="detail-label">Arquivos</span>
                                            <span class="detail-value">{{ automation.fileSelection.values.length }}</span>
                                        </div>
                                    </div>
                                    <div class="detail-item detail-item--success">
                                        <div class="detail-icon">
                                            <sw-icon name="users" />
                                        </div>
                                        <div class="detail-content">
                                            <span class="detail-label">Destinatários</span>
                                            <span class="detail-value">{{ automation.recipients.length }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="card-footer">
                            <div class="execution-info">
                                <div class="execution-detail">
                                    <sw-icon name="calendar" />
                                    <span>Último envio: {{ formatDate(automation.lastExecution) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Estado vazio -->
        <div class="empty-section" v-else-if="automations.length === 0">
            <EmptyState
                icon="mail"
                title="Nenhuma automação criada"
                description="Crie sua primeira automação de envio para começar a automatizar o envio de arquivos por email"
            >
                <template #actions>
                    <sw-button
                        name="Criar Primeira Automação"
                        color="blue"
                        @click="openCreateModal"
                    />
                </template>
            </EmptyState>
        </div>

        <!-- Nenhum resultado -->
        <div class="no-results-section" v-else>
            <EmptyState
                icon="search"
                title="Nenhum resultado encontrado"
                description="Tente ajustar os filtros de busca ou usar termos diferentes"
            >
                <template #actions>
                    <sw-button
                        name="Criar Nova Automação"
                        color="blue"
                        @click="openCreateModal"
                    />
                </template>
            </EmptyState>
        </div>

        <!-- Modal de Criação -->
        <sw-modal
            :visible="newAutomationModalVisible"
            @update="newAutomationModalVisible = $event">

            <template #header>
                <ModalHeader
                    icon="mail"
                    title="Nova Automação de Email"
                    subtitle="Configure envios automáticos de documentos por email"
                    color="blue"
                />
            </template>

            <template #content>
                <NewAutomationModal
                    @automation-created="handleAutomationCreated"
                />
            </template>
        </sw-modal>

        <!-- Modal de Edição -->
        <sw-modal
            :visible="editModalVisible"
            @update="editModalVisible = $event">

            <template #header>
                <ModalHeader
                    icon="mail"
                    title="Editar Automação"
                    subtitle="Configure envios automáticos de documentos por email"
                    color="blue"
                />
            </template>

            <template #content>
                <automation-email-modal
                    :automation-data="selectedAutomation"
                    @close="closeModal"
                    @save="saveAutomation"
                />
            </template>
        </sw-modal>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted } from 'vue';

    import AutomationEmailModal from '@Component/automation/AutomationEmailModal.vue';
    import NewAutomationModal from '@Component/automation/NewAutomationModal.vue';
    import Box from '@Component/boxs/BoxComponent.vue';
    import ModalHeader from '@Component/modals/ModalHeader.vue';
    import FilterSection from '@Component/filters/FilterSection.vue';
    import EmptyState from '@Component/empty-states/EmptyState.vue';

    import { BOX_VARIANTS } from '@/constants';
    import { mockAutomationEmails } from '@/data/mockData';
    import { notify } from 'sysweb-vue-components/src/global';

    // Estado
    const automations = ref([]);
    const searchQuery = ref('');
    const editModalVisible = ref(false);
    const newAutomationModalVisible = ref(false);
    const selectedAutomation = ref(null);

    // Ações do header
    const headerActions = computed(() => [
        {
            type: 'button',
            icon: 'plus',
            text: 'Nova Automação',
            title: 'Criar nova automação de email',
            key: 'create'
        }
    ]);

    // Configuração dos filtros para o FilterSection
    const filterOptions = ref([
        {
            key: 'status',
            label: 'Status',
            options: [
                { value: '', label: 'Todos os Status' },
                { value: 'active', label: 'Ativo' },
                { value: 'inactive', label: 'Inativo' },
                { value: 'waiting', label: 'Aguardando Arquivos' }
            ]
        },
        {
            key: 'frequency',
            label: 'Frequência',
            options: [
                { value: '', label: 'Todas as Frequências' },
                { value: 'once', label: 'Uma vez' },
                { value: 'daily', label: 'Diário' },
                { value: 'weekly', label: 'Semanal' },
                { value: 'monthly', label: 'Mensal' }
            ]
        }
    ]);

    const filterValues = ref({
        status: '',
        frequency: ''
    });

    // Computed
    const filteredAutomations = computed(() => {
        let filtered = automations.value;

        if (searchQuery.value) {
            filtered = filtered.filter(automation =>
                automation.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
                automation.description.toLowerCase().includes(searchQuery.value.toLowerCase())
            );
        }

        if (filterValues.value.status) {
            filtered = filtered.filter(automation => {
                if (filterValues.value.status === 'active') return automation.active && !automation.waiting;
                if (filterValues.value.status === 'inactive') return !automation.active && !automation.waiting;
                if (filterValues.value.status === 'waiting') return automation.waiting;
                return true;
            });
        }

        if (filterValues.value.frequency) {
            filtered = filtered.filter(automation =>
                automation.frequency === filterValues.value.frequency
            );
        }

        return filtered;
    });

    // Total de emails já enviados no mês (histórico fixo)
    const emailsSent = ref(247); // Valor fixo que não diminui

    // Calcular emails programados apenas de automações ativas
    const emailsProgrammedFromActive = computed(() => {
        return automations.value.reduce((total, automation) => {
            if (automation.active && !automation.waiting) {
                const scheduledCount = automation.frequency === 'weekly' ? 4 :
                                      automation.frequency === 'monthly' ? 1 :
                                      automation.frequency === 'daily' ? 30 : 0;
                return total + scheduledCount;
            }
            return total;
        }, 0);
    });

    const emailsScheduled = computed(() => emailsProgrammedFromActive.value);

    const activeAutomationsCount = computed(() => {
        return automations.value.filter(automation => automation.active).length;
    });

    const monthlyLimit = computed(() => {
        // Simular limite baseado no plano do usuário
        return 1000; // Exemplo: 1000 envios por mês
    });

    // Métodos
    const updateFilter = ({ key, value }) => {
        filterValues.value[key] = value;
    };

    const clearFilters = () => {
        searchQuery.value = '';
        filterValues.value = {
            status: '',
            frequency: ''
        };
    };
    const loadAutomations = () => {
        automations.value = mockAutomationEmails;
    };

    const handleHeaderAction = (action) => {
        console.log('Action received:', action);
        if (action.key === 'create') {
            openCreateModal();
        }
    };

    const openCreateModal = () => {
        console.log('Abrindo modal de criação...');
        newAutomationModalVisible.value = true;
        console.log('newAutomationModalVisible:', newAutomationModalVisible.value);
    };

    const editAutomation = (automation) => {
        selectedAutomation.value = { ...automation };
        editModalVisible.value = true;
    };

    const closeModal = () => {
        editModalVisible.value = false;
        selectedAutomation.value = null;
    };

    const saveAutomation = (automationData) => {
        if (automationData.id) {
            // Editar existente
            const index = automations.value.findIndex(a => a.id === automationData.id);
            if (index !== -1) {
                automations.value[index] = { ...automationData };
            }
        } else {
            // Criar novo
            const newAutomation = {
                ...automationData,
                id: Date.now(),
                lastExecution: null,
                monthlyCount: 0,
                totalSent: 0,
                successfulSent: 0
            };
            automations.value.push(newAutomation);
        }
        closeModal();
    };

    const toggleAutomation = (automation) => {
        automation.active = !automation.active;
        notify(automation.active ?
            `A automação "${automation.title}" foi ativada com sucesso.` :
            `A automação "${automation.title}" foi pausada.`,
            automation.active ? 'success' : 'warning');

        // Nota: emails já enviados (emailsSent) permanecem inalterados
        // Apenas emails programados (emailsScheduled) são recalculados
    };

    const deleteAutomation = (automation) => {
        const index = automations.value.findIndex(a => a.id === automation.id);
        if (index !== -1) {
            automations.value.splice(index, 1);
        }
    };

    const handleAutomationCreated = (newAutomation) => {
        automations.value.push(newAutomation);
        newAutomationModalVisible.value = false;
        notify(`A automação "${newAutomation.name}" foi criada com sucesso.`, 'success');
    };

    const formatFrequency = (frequency) => {
        const frequencyMap = {
            'once': 'Uma vez',
            'daily': 'Diário',
            'weekly': 'Semanal',
            'monthly': 'Mensal'
        };
        return frequencyMap[frequency] || frequency;
    };

    const formatDate = (date) => {
        if (!date) return 'Nunca';
        return new Date(date).toLocaleDateString('pt-BR');
    };

    const getStatusClass = (automation) => {
        if (automation.waiting) return 'waiting';
        return automation.active ? 'active' : 'inactive';
    };

    const getStatusLabel = (automation) => {
        if (automation.waiting) return 'Aguardando Arquivos';
        return automation.active ? 'Ativo' : 'Inativo';
    };

    // Lifecycle
    onMounted(() => {
        loadAutomations();
    });
</script>

<style lang="scss" scoped>
    @import '@/assets/css/mediaScreen.scss';
    @import '@/assets/css/components.scss';

    .automation-emails {

        // Seção de estatísticas moderna e limpa
        .stats-section {
            margin-bottom: 2em;

            .row {
                align-items: stretch;
            }

            .stat-card {
                background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
                border-radius: 1.25em;
                padding: 1.5em;
                border: 1px solid rgba(226, 232, 240, 0.8);
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                position: relative;
                overflow: hidden;
                height: 100%;
                display: flex;
                flex-direction: column;

                &::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: linear-gradient(90deg, transparent, var(--linkColor), transparent);
                    opacity: 0;
                    transition: opacity 0.3s ease;
                }

                &:hover {
                    box-shadow:
                        0 8px 32px rgba(0, 0, 0, 0.08),
                        0 1px 2px rgba(0, 0, 0, 0.05);
                    border-color: rgba(59, 130, 246, 0.2);

                    &::before {
                        opacity: 1;
                    }
                }

                .stat-header {
                    display: flex;
                    align-items: flex-start;
                    gap: 1.25em;
                    margin-bottom: 1.25em;
                    flex: 1;

                    .stat-icon {
                        width: 3.25em;
                        height: 3.25em;
                        border-radius: 1em;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 1.125em;
                        flex-shrink: 0;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                        position: relative;

                        &::after {
                            content: '';
                            position: absolute;
                            inset: 0;
                            border-radius: inherit;
                            background: linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.05));
                            pointer-events: none;
                        }
                    }

                    .stat-info {
                        flex: 1;

                        .stat-label {
                            font-size: 0.875em;
                            color: rgba(100, 116, 139, 0.8);
                            font-weight: 600;
                            margin-bottom: 0.5em;
                            display: block;
                            text-transform: uppercase;
                            letter-spacing: 0.025em;
                        }

                        .stat-numbers {
                            display: flex;
                            align-items: baseline;
                            gap: 0.5em;
                            margin-bottom: 0.25em;

                            .stat-main {
                                font-size: 2.25em;
                                font-weight: 800;
                                color: var(--textPrimary);
                                line-height: 1;
                            }

                            .stat-secondary {
                                font-size: 0.9em;
                                color: rgba(100, 116, 139, 0.7);
                                font-weight: 500;
                            }
                        }

                        .stat-breakdown {
                            display: flex;
                            flex-direction: column;
                            gap: 0.25em;
                            margin-top: 0.75em;

                            .breakdown-item {
                                display: flex;
                                justify-content: space-between;
                                align-items: center;
                                font-size: 0.75em;

                                .breakdown-label {
                                    color: var(--textSecondary);
                                    font-weight: 500;
                                }

                                .breakdown-value {
                                    color: var(--textPrimary);
                                    font-weight: 600;
                                    font-size: 1em;

                                    &--scheduled {
                                        color: var(--colorBlue);
                                        font-weight: 700;
                                    }

                                    &--limit {
                                        color: var(--textSecondary);
                                        font-weight: 700;
                                    }
                                }
                            }
                        }
                    }
                }

                .stat-progress {
                    display: flex;
                    align-items: center;
                    gap: 1em;
                    margin-top: 0.5em;

                    .progress-bar {
                        flex: 1;
                        height: 0.5em;
                        background: rgba(226, 232, 240, 0.6);
                        border-radius: 1em;
                        overflow: hidden;
                        position: relative;
                        backdrop-filter: blur(10px);

                        .progress-fill {
                            height: 100%;
                            border-radius: inherit;
                            transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
                            position: relative;
                            overflow: hidden;
                            background: #10b981;

                            &::after {
                                content: '';
                                position: absolute;
                                top: 0;
                                left: 0;
                                right: 0;
                                bottom: 0;
                                background: linear-gradient(90deg,
                                    rgba(255,255,255,0) 0%,
                                    rgba(255,255,255,0.4) 50%,
                                    rgba(255,255,255,0) 100%);
                                animation: shimmer 2s infinite;
                            }
                        }
                    }

                    .progress-percentage {
                        font-size: 0.8em;
                        font-weight: 700;
                        min-width: 2.5em;
                        text-align: right;
                        color: var(--textPrimary);
                    }
                }

                // Cards compactos (para limites e programados)
                &.stat-card--compact {
                    padding: 1.25em;

                    .stat-header {
                        margin-bottom: 1em;

                        .stat-icon {
                            width: 2.75em;
                            height: 2.75em;
                            font-size: 1em;
                        }

                        .stat-info {
                            .stat-numbers {
                                gap: 0.375em;

                                .stat-main {
                                    font-size: 1.75em;
                                    font-weight: 700;
                                }

                                .stat-secondary {
                                    font-size: 0.8em;
                                }
                                }
                            }
                        }

                    .stat-progress {
                        margin-top: 0.375em;
                        gap: 0.75em;

                        .progress-bar {
                            height: 0.375em;
                        }

                        .progress-percentage {
                            font-size: 0.75em;
                            min-width: 2em;
                        }
                    }

                    &.stat-card--primary {
                        .stat-icon {
                            background: linear-gradient(135deg, #3b82f6, #1d4ed8);
                            color: white;
                        }

                        .progress-bar {
                            background: rgba(59, 130, 246, 0.15);

                            .progress-fill {
                                background: #3b82f6;
                            }
                        }

                        .progress-percentage {
                            color: #3b82f6;
                        }
                    }

                    &.stat-card--success {
                        .stat-icon {
                            background: linear-gradient(135deg, #10b981, #059669);
                            color: white;
                        }

                        .progress-bar {
                            background: rgba(16, 185, 129, 0.15);

                            .progress-fill {
                                background: #10b981;
                            }
                        }

                        .progress-percentage {
                            color: #10b981;
                        }
                    }
                }
            }
        }


        // Seção de automações premium (agora fora do Box)
        .automations-section {
            background: var(--backgroundColorLighter);
            border-radius: 1.5em;
            padding: 25px 0px;
            border: 1px solid var(--borderColorLight);
        }

        .automation-card {
            position: relative;
            background: white;
            border-radius: 1.5em;
            padding: 0;
            margin-bottom: 2em;
            border: 1px solid var(--borderColorLight);
            transition: all 0.3s ease;
            overflow: hidden;

            &:hover {
                box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
                border-color: var(--linkColor);
            }

            &.inactive {
                opacity: 0.7;

                &:hover {
                    opacity: 0.9;
                }
            }

            .card-status-bar {
                height: 4px;
                width: 100%;

                &.active {
                    background: linear-gradient(90deg, #10b981, #059669);
                }

                &.inactive {
                    background: linear-gradient(90deg, #f59e0b, #d97706);
                }

                &.waiting {
                    background: linear-gradient(90deg, #8b5cf6, #7c3aed);
                }
            }

            .card-header {
                padding: 1.75em 1.75em 1em;

                .automation-header-content {
                    display: flex;
                    flex-direction: column;
                    gap: 1em;
                    margin-bottom: 1em;

                    .automation-title-section {
                        flex: 1;

                        h4 {
                            font-size: 1.25em;
                            font-weight: 700;
                            color: var(--textPrimary);
                        }
                    }

                    .automation-actions {
                        width: 100%;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 1em;

                        .status-badge {
                            display: flex;
                            align-items: center;
                            gap: 0.5em;
                            padding: 0.375em 0.875em;
                            border-radius: 0.75em;
                            font-size: 0.75em;
                            font-weight: 600;
                            flex-shrink: 0;

                            .status-dot {
                                width: 0.5em;
                                height: 0.5em;
                                border-radius: 50%;
                            }

                            &.active {
                                background: rgba(16, 185, 129, 0.1);
                                color: #10b981;
                                border: 1px solid rgba(16, 185, 129, 0.2);

                                .status-dot {
                                    background: #10b981;
                                }
                            }

                            &.inactive {
                                background: rgba(245, 158, 11, 0.1);
                                color: #f59e0b;
                                border: 1px solid rgba(245, 158, 11, 0.2);

                                .status-dot {
                                    background: #f59e0b;
                                }
                            }

                            &.waiting {
                                background: rgba(139, 92, 246, 0.1);
                                color: #8b5cf6;
                                border: 1px solid rgba(139, 92, 246, 0.2);

                                .status-dot {
                                    background: #8b5cf6;
                                }
                            }
                        }

                        .action-buttons {
                            display: flex;
                            gap: 0.5em;

                            .action-btn {
                                width: 2.25em;
                                height: 2.25em;
                                border-radius: 0.75em;
                                border: 1px solid var(--borderColorLight);
                                background: white;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                cursor: pointer;
                                transition: all 0.2s ease;
                                font-size: 0.875em;

                                &:hover {
                                    transform: scale(1.05);
                                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                                }

                                &.action-btn--toggle {
                                    &.play {
                                        color: #10b981;
                                        border-color: rgba(16, 185, 129, 0.3);

                                        &:hover {
                                            background: rgba(16, 185, 129, 0.1);
                                        }
                                    }

                                    &.pause {
                                        color: #f59e0b;
                                        border-color: rgba(245, 158, 11, 0.3);

                                        &:hover {
                                            background: rgba(245, 158, 11, 0.1);
                                        }
                                    }
                                }

                                &.action-btn--edit {
                                    color: #3b82f6;
                                    border-color: rgba(59, 130, 246, 0.3);

                                    &:hover {
                                        background: rgba(59, 130, 246, 0.1);
                                    }
                                }

                                &.action-btn--delete {
                                    color: #ef4444;
                                    border-color: rgba(239, 68, 68, 0.3);

                                    &:hover {
                                        background: rgba(239, 68, 68, 0.1);
                                        color: #dc2626;
                                    }
                                }
                            }
                        }
                    }
                }

                .automation-description {
                    p {
                        font-size: 0.875em;
                        color: var(--textSecondary);
                        margin: 0;
                        line-height: 1.5;
                    }
                }

                .waiting-section {
                    margin-top: 1.5em;
                    padding: 1.25em;
                    background: rgba(139, 92, 246, 0.04);
                    border: 1px solid rgba(139, 92, 246, 0.15);
                    border-radius: 1em;

                    .waiting-info {
                        margin-bottom: 1.25em;

                        .waiting-header {
                            display: flex;
                            align-items: center;
                            gap: 0.75em;
                            margin-bottom: 1em;
                            color: #8b5cf6;
                            font-weight: 600;
                            font-size: 0.875em;

                            .sw-icon {
                                font-size: 1.25em;
                            }
                        }

                        .waiting-details {
                            display: flex;
                            flex-direction: column;
                            gap: 0.875em;

                            .upload-link {
                                display: flex;
                                flex-direction: column;
                                gap: 0.375em;

                                .link-label {
                                    font-size: 0.75em;
                                    color: var(--textSecondary);
                                    font-weight: 500;
                                    text-transform: uppercase;
                                    letter-spacing: 0.025em;
                                }

                                .upload-url {
                                    display: flex;
                                    align-items: center;
                                    gap: 0.5em;
                                    color: #8b5cf6;
                                    text-decoration: none;
                                    font-size: 0.8em;
                                    font-weight: 500;
                                    padding: 0.5em 0.75em;
                                    background: rgba(139, 92, 246, 0.08);
                                    border-radius: 0.5em;
                                    border: 1px solid rgba(139, 92, 246, 0.2);
                                    transition: all 0.2s ease;

                                    &:hover {
                                        background: rgba(139, 92, 246, 0.12);
                                    }

                                    .sw-icon {
                                        font-size: 0.875em;
                                    }
                                }
                            }

                            .deadline-info {
                                display: flex;
                                align-items: center;
                                gap: 0.5em;
                                font-size: 0.8em;
                                color: var(--textSecondary);
                                font-weight: 500;

                                .sw-icon {
                                    color: #f59e0b;
                                    font-size: 1em;
                                }
                            }
                        }
                    }

                    .expected-files {
                        .expected-label {
                            display: block;
                            font-size: 0.75em;
                            color: var(--textSecondary);
                            font-weight: 500;
                            text-transform: uppercase;
                            letter-spacing: 0.025em;
                            margin-bottom: 0.75em;
                        }

                        .files-list {
                            display: flex;
                            flex-wrap: wrap;
                            gap: 0.5em;

                            .file-tag {
                                padding: 0.375em 0.75em;
                                background: rgba(139, 92, 246, 0.1);
                                color: #8b5cf6;
                                border: 1px solid rgba(139, 92, 246, 0.2);
                                border-radius: 0.5em;
                                font-size: 0.75em;
                                font-weight: 500;
                            }
                        }
                    }
                }
            }

            .card-content {
                padding: 0 1.75em;
                margin-bottom: 1.5em;

                .automation-metrics {
                    .metric-row {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 1.5em;
                        margin-bottom: 1em;

                        &:last-child {
                            margin-bottom: 0;
                        }

                        .detail-item {
                            display: flex;
                            align-items: flex-start;
                            gap: 0.875em;
                            padding: 1em;
                            background: rgba(255, 255, 255, 0.6);
                            border-radius: 0.875em;
                            border: 1px solid rgba(255, 255, 255, 0.8);
                            transition: all 0.2s ease;

                            &:hover {
                                background: white;
                                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                            }

                            .detail-icon {
                                width: 2.25em;
                                height: 2.25em;
                                border-radius: 0.625em;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                flex-shrink: 0;
                                font-size: 0.875em;
                            }

                            &.detail-item--primary .detail-icon {
                                background: rgba(59, 130, 246, 0.1);
                                color: #3b82f6;
                            }

                            &.detail-item--secondary .detail-icon {
                                background: rgba(139, 69, 19, 0.1);
                                color: #8b4513;
                            }

                            &.detail-item--info .detail-icon {
                                background: rgba(107, 114, 128, 0.1);
                                color: #6b7280;
                            }

                            &.detail-item--success .detail-icon {
                                background: rgba(16, 185, 129, 0.1);
                                color: #10b981;
                            }

                            .detail-content {
                                display: flex;
                                flex-direction: column;
                                gap: 0.125em;

                                .detail-label {
                                    font-size: 0.75em;
                                    color: var(--textSecondary);
                                    font-weight: 500;
                                    text-transform: uppercase;
                                    letter-spacing: 0.025em;
                                }

                                .detail-value {
                                    font-size: 0.875em;
                                    color: var(--textPrimary);
                                    font-weight: 600;
                                }
                            }
                        }
                    }
                }
            }

            .card-footer {
                padding: 1.25em 1.75em 1.75em;
                border-top: 1px solid rgba(255, 255, 255, 0.8);
                background: rgba(255, 255, 255, 0.4);

                .execution-info {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;

                    .execution-detail {
                        display: flex;
                        align-items: center;
                        gap: 0.5em;
                        font-size: 0.8em;
                        color: var(--textSecondary);
                        font-weight: 500;

                        .sw-icon {
                            font-size: 1em;
                            color: var(--linkColor);
                        }
                    }

            }
        }

        // Estados vazios (usando EmptyState component)
        .empty-section,
        .no-results-section {
            background: var(--backgroundColorLighter);
            border-radius: 1.5em;
            border: 1px solid var(--borderColorLight);
        }
    }

    @include screen_medias(m) {
        .automation-emails {
            .stats-section .stat-card {
                height: auto;
                min-height: 140px;
            }
        }
    }

    @include screen_medias(s) {
        .automation-emails {
            .stats-section {
                .stat-card {
                    padding: 1.25em;
                    border-radius: 1em;

                    .stat-header {
                        gap: 1em;
                        margin-bottom: 1em;

                        .stat-icon {
                            width: 2.75em;
                            height: 2.75em;
                            font-size: 1em;
                            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                        }

                        .stat-info {
                            .stat-label {
                                font-size: 0.8em;
                                margin-bottom: 0.375em;
                            }

                            .stat-numbers {
                                .stat-main {
                                    font-size: 1.75em;
                                }

                                .stat-secondary {
                                    font-size: 0.85em;
                                }
                            }
                        }
                    }

                    .stat-progress {
                        gap: 0.75em;
                        margin-top: 0.375em;

                        .progress-bar {
                            height: 0.375em;
                        }

                        .progress-percentage {
                            font-size: 0.75em;
                            min-width: 2em;
                        }
                    }
                }
            }


            .automations-section {
                .section-header {
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 1em;

                    .results-count {
                        align-self: flex-start;
                    }
                }

                .automation-grid {
                    grid-template-columns: 1fr;
                    gap: 1.5em;

                    .automation-card {
                        .card-header {
                            padding: 1.5em 1.5em 0;

                            .automation-actions {
                                top: 1.5em;
                                right: 1.5em;
                            }
                        }

                        .card-content {
                            padding: 0 1.5em;

                            .automation-metrics .metric-row {
                                grid-template-columns: 1fr;
                                gap: 1em;
                            }
                        }

                        .card-footer {
                            padding: 1.25em 1.5em 1.5em;

                            .execution-info {
                                flex-direction: column;
                                align-items: flex-start;
                                gap: 0.75em;
                            }
                        }
                    }
                }
            }
        }
    }

    // Animação shimmer para progress bars
    @keyframes shimmer {
        0% {
            transform: translateX(-100%);
        }
        100% {
            transform: translateX(100%);
        }
    }
}
</style>